/**
 * Viewport event scheduler and interaction state manager.
 *
 * Why this file exists:
 * - Drag/zoom emits high-frequency events and can trigger excessive recomputation.
 * - Centralizing scheduling keeps marker updates responsive without flooding work.
 *
 * Responsibilities:
 * - Register and clear map viewport listeners.
 * - Track interaction state (`isInteracting`) across drag/zoom/idle phases.
 * - Drive visibility recalculation exclusively from `idle` — the authoritative
 *   signal that Maps has finished animating.
 * - Coalesce recalculations through requestAnimationFrame.
 *
 * Listener lifecycle:
 * - Listeners are registered lazily on the first `scheduleUpdate` call.
 *   This defers `onMapEvent` until after the map is initialized, avoiding
 *   "Map is not initialized" errors when the controller is constructed early.
 * - `clearListeners` removes them (e.g. on map destroy).
 * - Callers do not need to perform any manual setup.
 */
export default function createViewportController({
  /**
   * Called when the viewport has settled and markers should be recomputed.
   * Implementations should trigger a visibility recalculation —
   * e.g. call updateMarkersVisibility(), emit to an RxJS Subject, or dispatch to a store.
   * Must be synchronous and side-effect-free with respect to map state.
   */
  onUpdate,
  onMapEvent,
  // Minimum ms between two consecutive idle-triggered updates.
  // Prevents rapid idle events (e.g. 5 zoom clicks each settling quickly) and
  // idle firing right after a syncMarkers rAF already ran the update from
  // each queuing a redundant rAF + visibility pass.
  // Direct scheduleUpdate() calls (syncMarkers, setMarkerBehavior) are NOT
  // subject to this cooldown — they represent data changes that should
  // always trigger an immediate rAF when the map is idle.
  idleCooldownMs = 50,
  now = () => Date.now(),
  requestFrame = (cb) => window.requestAnimationFrame(cb),
  cancelFrame = (id) => window.cancelAnimationFrame(id),
}) {
  const state = {
    listeners: [],
    queuedFrame: null,
    isInteracting: false,
    // Timestamp of the last completed onUpdate() call. Used by the idle
    // cooldown to suppress redundant post-idle rAFs.
    lastUpdateTime: 0,
    idleCooldownTimer: null,
  };

  function scheduleUpdateCore() {
    if (state.queuedFrame) {
      return;
    }

    state.queuedFrame = requestFrame(() => {
      state.queuedFrame = null;
      state.lastUpdateTime = now();
      onUpdate?.();
    });
  }

  // Idle-specific scheduler: respects the cooldown window so that a rapid
  // sequence of idle events (or idle firing immediately after a data-driven
  // rAF) doesn’t queue redundant visibility passes.
  function scheduleUpdateFromIdle() {
    const elapsed = now() - state.lastUpdateTime;

    if (elapsed >= idleCooldownMs) {
      scheduleUpdateCore();
      return;
    }

    // Already within the cooldown window. Schedule a single deferred rAF
    // so the last idle in a burst still produces one update.
    if (!state.idleCooldownTimer) {
      state.idleCooldownTimer = setTimeout(() => {
        state.idleCooldownTimer = null;
        scheduleUpdateCore();
      }, idleCooldownMs - elapsed);
    }
  }

  function ensureListeners() {
    if (state.listeners.length > 0 || !onMapEvent) {
      return;
    }

    state.listeners.push(onMapEvent('dragstart', () => {
      state.isInteracting = true;
    }));

    state.listeners.push(onMapEvent('zoom_changed', () => {
      state.isInteracting = true;
    }));

    state.listeners.push(onMapEvent('idle', () => {
      state.isInteracting = false;
      scheduleUpdateFromIdle();
    }));
  }

  function scheduleUpdate() {
    ensureListeners();
    scheduleUpdateCore();
  }

  function clearListeners() {
    state.listeners.forEach((unsubscribe) => unsubscribe?.());
    state.listeners = [];
  }

  function cancelScheduledUpdate() {
    if (state.queuedFrame) {
      cancelFrame(state.queuedFrame);
      state.queuedFrame = null;
    }

    if (state.idleCooldownTimer) {
      clearTimeout(state.idleCooldownTimer);
      state.idleCooldownTimer = null;
    }
  }

  function getIsInteracting() {
    return state.isInteracting;
  }

  function resetTimingState() {
    cancelScheduledUpdate();
    state.isInteracting = false;
    state.lastUpdateTime = 0;
  }

  function destroy() {
    resetTimingState();
    clearListeners();
  }

  return {
    clearListeners,
    scheduleUpdate,
    cancelScheduledUpdate,
    getIsInteracting,
    destroy,
  };
}